/**
 * Date parser using Sugar.js for robust date creation and regex for recurrence detection.
 */

// Remove chrono import
// import * as chrono from 'chrono-node';
import Sugar from 'sugar';

// Keep only patterns relevant for *recurrence* detection
const RECURRENCE_PATTERNS = {
  everyDay: /\b(every)\s*(day|daily)\b|\b(everyday|daily)\b/i,
  everyDayOfWeek: /\b(every)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i,
  everyWeek: /\b(every)\s+week\b|\b(weekly)\b/i, // Added 'weekly' as an alternative
  everyWeekOnDay: /\b(every)\s+week\s+on\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b|\b(weekly)\s+on\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i, // Added 'weekly on X' pattern
  // REMOVED old lastDayOfMonth pattern - It was too broad
  // lastDayOfMonth: /\b(last\s+day\s+of)\s+(?:every|each)\s+month\b/i,
  // NEW pattern specifically for *recurring* last day - Use non-capturing group
  lastDayOfEveryMonth: /\b(last\s+day\s+of\s+(?:the\s+)?month)\s+(?:every|each)\s+month\b/i,
  everyMonth: /\b(every)\s+month\b|\b(monthly)\b/i, // Added 'monthly' as an alternative
  everyMonthOnDayOfMonth: /\b(every)\s+month\s+on\s+(?:the\s+)?(\d{1,2})(st|nd|rd|th)?\b/i, // Specific day of month
  everyYear: /\b(every)\s+year\b|\b(yearly)\b/i, // Added 'yearly' as an alternative
  everyMonthOnDayOfWeek: /\b(?:(first|second|third|fourth|last)\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+of\s+(every|each)\s+month\b/i,
  dayOfWeekOfEveryWeek: /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+of\s+(every|each)\s+week\b/i,
  biWeekly: /\b(bi-weekly|biweekly|every other week)\b/i,
  everyYearOnDayMonth: /\b(every)\s+year\s+on\s+(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i,
  // Added new pattern for "on X month every year"
  dayMonthEveryYear: /\b(?:on\s+)?(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(?:every|each)\s+year\b/i,
  dayOfMonthOfEveryMonth: /\b(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?(every|each)\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i,
  // Improve the pattern to be more flexible and match more variations
  // Updated to also match "this task is due on 9th of every month" format and "this is due 15th of every month"
  ordinalDayOfEveryMonth: /\b(?:(?:on|due on|is due)\s+)?(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?(?:every|each)\s+month\b/i,
  // Add a new pattern for "this task is due on 8th of every month" format
  taskDueOnDayOfEveryMonth: /\bthis\s+task\s+is\s+due\s+on\s+(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?(?:every|each)\s+month\b/i, // This should match "this task is due on 9th of every month"
  // NEW pattern for '[weekday] every week'
  dayOfWeekEveryWeek: /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+(every|each)\s+week\b/i,
};

// Map day names to numbers (0 for Sunday, 1 for Monday, etc.) - Keep for recurrence logic
const DAYS_OF_WEEK = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
};

// NEW: Map month names/abbreviations to numbers (0-11)
const MONTHS_MAP = {
    jan: 0, january: 0,
    feb: 1, february: 1,
    mar: 2, march: 2,
    apr: 3, april: 3,
    may: 4, // may is 4
    jun: 5, june: 5,
    jul: 6, july: 6,
    aug: 7, august: 7,
    sep: 8, september: 8,
    oct: 9, october: 9,
    nov: 10, november: 10,
    dec: 11, december: 11
};

// Simple regex to directly match common time expressions without requiring a prefix
const SIMPLE_TIME_REGEX = /\b((?:the\s+)?day\s+after\s+tomorrow|tomorrow|today|tonight|next\s+(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday)|next\s+week|next\s+month)\b/i;

// ─── Date-intent gate ──────────────────────────────────────────────────────
// Without this gate, Sugar.Date.create() interprets bare integers as years
// (e.g. "owe 2010" → 2010-01-01) because Sugar.js will pull any year-shaped
// token out of free-form text. The gate requires an explicit date hint
// (relative word, day/month name with context, slash/dash date, time, etc.)
// before letting Sugar parse the input.
const MONTH_NAMES_RE = '(?:january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept|sep|oct|nov|dec)';
const DAY_NAMES_RE = '(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tues|tue|wed|thurs|thur|thu|fri|sat|sun)';

// "3 days later", "two weeks out", "in a couple of days" — whole days/weeks/months/years from today.
const AMOUNT_RE = '(?:\\d+|a\\s+couple\\s+of|couple\\s+of|a\\s+few|an?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)';
const RELATIVE_AMOUNT_RE = new RegExp(
  `\\b(?:in\\s+(${AMOUNT_RE})\\s+(day|week|month|year)s?|(${AMOUNT_RE})\\s+(day|week|month|year)s?\\s+(?:later|out|hence|from\\s+(?:now|today)))\\b`, 'i');
// "early next week", "late next week", "mid next week", "end of next week" (also "this week")
const WEEK_PART_RE = /\b(early|mid|late|(?:the\s+)?end\s+of)[\s-]+(next|this)\s+week\b/i;

const DATE_INTENT_PATTERNS = [
  RELATIVE_AMOUNT_RE,
  WEEK_PART_RE,
  // Explicit relative time keywords
  /\b(today|tomorrow|tonight|yesterday)\b/i,
  // Standalone day names (Monday, Mon, Tue, ...)
  new RegExp(`\\b${DAY_NAMES_RE}\\b`, 'i'),
  // Month + day (April 30, Apr 30, May 5)
  new RegExp(`\\b${MONTH_NAMES_RE}\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?\\b`, 'i'),
  // Day + (of) + month (5 May, 9th of June)
  new RegExp(`\\b\\d{1,2}(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH_NAMES_RE}\\b`, 'i'),
  // "next/last/this/coming/upcoming + period"
  /\b(?:next|last|this|coming|upcoming)\s+(?:week|month|year|weekend)\b/i,
  // "next/last/this/coming/upcoming + day name"
  new RegExp(`\\b(?:next|last|this|coming|upcoming)\\s+${DAY_NAMES_RE}\\b`, 'i'),
  // "next/last/this/coming/upcoming + month name"
  new RegExp(`\\b(?:next|last|this|coming|upcoming)\\s+${MONTH_NAMES_RE}\\b`, 'i'),
  // "in N units" / "in a unit"
  /\bin\s+(?:a|an|\d+)\s+(?:day|days|week|weeks|month|months|year|years|hour|hours|min|mins|minute|minutes)\b/i,
  // "N units from now" / "N units ago"
  /\b\d+\s+(?:day|days|week|weeks|month|months|year|years|hour|hours|min|mins|minute|minutes)\s+(?:from\s+now|ago)\b/i,
  // Numeric date: 4/15, 4/15/2026, 4-15, 4-15-2026
  /\b\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?\b/,
  // ISO-style: 2026-04-15
  /\b\d{4}[/-]\d{1,2}[/-]\d{1,2}\b/,
  // Time with am/pm
  /\b\d{1,2}(?::\d{2})?\s*(?:am|pm|a\.m\.|p\.m\.)\b/i,
  // Time with colon (5:30, 17:00)
  /\b\d{1,2}:\d{2}\b/,
  // Noon / midnight
  /\b(?:noon|midnight)\b/i,
  // End/start/beginning of <period>
  /\b(?:end|start|beginning|middle)\s+of\s+(?:today|tomorrow|day|week|month|year|the\s+(?:day|week|month|year))\b/i,
];

export function hasDateIntent(text) {
  if (!text || typeof text !== 'string') return false;
  return DATE_INTENT_PATTERNS.some(re => re.test(text));
}

// Sanity-check Sugar's parsed date. Rejects results outside a plausible window
// for task due dates ([currentYear - 1, currentYear + 10]). Catches the
// "today owes 2010" case where date intent is real but Sugar grabs the wrong
// token from mixed text.
function isReasonablyDated(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return false;
  const now = new Date();
  const refYear = now.getFullYear();
  const parsedYear = date.getFullYear();
  return parsedYear >= refYear - 1 && parsedYear <= refYear + 10;
}

// Simple logging function
function logMatch(patternName, text, match) {
  console.log(`Sugar Date Util: Recurrence matched: [${patternName}] on text "${text}". Match details:`, match);
}

// Helper function to test all patterns on a text
function testPatterns(text) {
  for (const [patternName, regex] of Object.entries(RECURRENCE_PATTERNS)) {
    const match = text.match(regex);
    if (match) {
      console.log(`Pattern ${patternName} matched:`, match);
    }
  }
}

// Helper function to get the next occurrence of a specific day of week
function getThisOrNextDayOfWeek(date, dayOfWeek) {
  const resultDate = new Date(date.getTime());
  resultDate.setDate(date.getDate() + (7 + dayOfWeek - date.getDay()) % 7);
  return resultDate;
}

// Helper function to get the next occurrence of a specific day of week, forcing next week
function getNextDayOfWeek(date, dayOfWeek, forceNextWeek = false) {
  const resultDate = new Date(date.getTime());
  if (forceNextWeek) {
    // "next Friday" = that day in NEXT week (weeks start Sunday) — the same day Sugar gives for
    // "next Friday" typed alone. (Was date + 7, i.e. "next Friday" on a Thursday = Thursday.)
    resultDate.setDate(date.getDate() - date.getDay() + 7 + dayOfWeek);
  } else if (date.getDay() === dayOfWeek) {
    resultDate.setDate(date.getDate() + 7);
  } else {
    resultDate.setDate(date.getDate() + (7 + dayOfWeek - date.getDay()) % 7);
  }
  return resultDate;
}

// Helper function to get the next occurrence of a specific day of month
function getNextDayOfMonth(date, dayOfMonth) {
  const resultDate = new Date(date.getTime());
  
  // Set to the target day of the current month
  resultDate.setDate(dayOfMonth);
  
  // If that date is in the past, move to next month
  if (resultDate < date) {
    resultDate.setMonth(resultDate.getMonth() + 1);
    resultDate.setDate(dayOfMonth);
  }
  
  // Handle cases where the day doesn't exist in the month (e.g., Feb 30)
  const actualMonth = resultDate.getMonth();
  resultDate.setDate(1); // Reset to first of month
  resultDate.setMonth(actualMonth + 1); // Move to first of next month
  resultDate.setDate(0); // Move back one day to last of target month
  
  // If the day of month is greater than the last day of the month, use the last day
  const lastDayOfMonth = resultDate.getDate();
  if (dayOfMonth > lastDayOfMonth) {
    resultDate.setDate(lastDayOfMonth);
  } else {
    resultDate.setDate(dayOfMonth);
  }
  
  return resultDate;
}

// Helper function to get the next occurrence of a specific ordinal day of week in a month
function getNextOrdinalDayOfWeekInMonth(date, dayOfWeek, ordinal) {
  const resultDate = new Date(date.getTime());
  
  // If ordinal is -1, it means the last occurrence of the day in the month
  if (ordinal === -1) {
    // Start from the last day of the current month
    resultDate.setDate(1);
    resultDate.setMonth(resultDate.getMonth() + 1);
    resultDate.setDate(0);
    
    // Move backward to find the last occurrence of the target day
    while (resultDate.getDay() !== dayOfWeek) {
      resultDate.setDate(resultDate.getDate() - 1);
    }
    
    // If the result is in the past, move to next month
    if (resultDate < date) {
      resultDate.setMonth(resultDate.getMonth() + 1);
      resultDate.setDate(1);
      resultDate.setMonth(resultDate.getMonth() + 1);
      resultDate.setDate(0);
      
      while (resultDate.getDay() !== dayOfWeek) {
        resultDate.setDate(resultDate.getDate() - 1);
      }
    }
  } else {
    // For 1st, 2nd, 3rd, 4th occurrence
    // Start from the first day of the current month
    resultDate.setDate(1);
    
    // If we're already past this month's occurrence, move to next month
    const currentMonth = resultDate.getMonth();
    const targetDate = getNthDayOfMonth(date.getFullYear(), currentMonth, dayOfWeek, ordinal);
    
    if (targetDate < date) {
      // Move to next month
      resultDate.setMonth(currentMonth + 1);
      resultDate.setDate(1);
      const nextMonth = resultDate.getMonth();
      resultDate.setDate(getNthDayOfMonth(resultDate.getFullYear(), nextMonth, dayOfWeek, ordinal).getDate());
    } else {
      resultDate.setDate(targetDate.getDate());
    }
  }
  
  return resultDate;
}

// Helper function to get the nth occurrence of a day in a month
function getNthDayOfMonth(year, month, dayOfWeek, n) {
  const result = new Date(year, month, 1);
  
  // Find the first occurrence of the day in the month
  while (result.getDay() !== dayOfWeek) {
    result.setDate(result.getDate() + 1);
  }
  
  // Add (n-1) weeks to get to the nth occurrence
  result.setDate(result.getDate() + (n - 1) * 7);
  
  return result;
}

// Helper function to get the next occurrence of a specific date in a year
function getNextYearlyDate(date, month, day) {
  const resultDate = new Date(date.getTime());
  
  // Set to the target month and day
  resultDate.setMonth(month);
  resultDate.setDate(day);
  
  // If that date is in the past, move to next year
  if (resultDate < date) {
    resultDate.setFullYear(resultDate.getFullYear() + 1);
  }
  
  // Handle cases where the day doesn't exist in the month (e.g., Feb 30)
  const actualMonth = resultDate.getMonth();
  if (actualMonth !== month) {
    // The day doesn't exist in this month, use the last day of the month
    resultDate.setDate(1); // Reset to first of month
    resultDate.setMonth(month + 1); // Move to first of next month
    resultDate.setDate(0); // Move back one day to last of target month
  }
  
  return resultDate;
}

// ─── Isolating a date phrase inside a sentence ─────────────────────────────
// Sugar.Date.create() only reads a string that is ENTIRELY a date, so "Call Mrs. Smith by Friday"
// set no due date. These patterns pull the date phrase out of the sentence, most specific first
// (a calendar date beats a weekday named in the same sentence). They are deliberately stricter than
// DATE_INTENT_PATTERNS: tooth ranges ("teeth 3-4"), fractions ("1/2 dose") and short day names used
// as words ("sat in the chair") must stay dateless.
const DATE_CUE_RE = '(?:by|on|before|until|till|due|for)';
const DAY_INDEX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
const dayIndexOf = (name) => DAY_INDEX[name.toLowerCase().slice(0, 3)];
const FULL_DAY_NAMES_RE = '(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday)';

// kind 'sugar' → Sugar reads the isolated phrase; 'weekday' → the coming day (today counts);
// 'nextWeekday' → that day in next week.
const SENTENCE_DATE_PHRASES = [
  { kind: 'sugar', re: /\b\d{4}-\d{1,2}-\d{1,2}\b/ },
  { kind: 'sugar', re: /\b\d{1,2}\/\d{1,2}\/(?:\d{4}|\d{2})\b/ },
  { kind: 'sugar', re: /\b\d{1,2}-\d{1,2}-\d{4}\b/ },
  // "on 10/12" — without a year a slash date needs a cue word, or "1/2 dose" would become Jan 2.
  { kind: 'sugar', re: new RegExp(`\\b${DATE_CUE_RE}\\s+(\\d{1,2}/\\d{1,2})\\b(?!/)`, 'i'), group: 1 },
  { kind: 'sugar', re: new RegExp(`\\b${MONTH_NAMES_RE}\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?\\b(?:,?\\s+\\d{4}\\b)?`, 'i') },
  { kind: 'sugar', re: new RegExp(`\\b\\d{1,2}(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH_NAMES_RE}\\b`, 'i') },
  { kind: 'nextWeekday', re: new RegExp(`\\bnext\\s+(${DAY_NAMES_RE})\\b`, 'i'), group: 1 },
  { kind: 'weekday', re: new RegExp(`\\b(?:this|coming|upcoming|${DATE_CUE_RE})\\s+(${DAY_NAMES_RE})\\b`, 'i'), group: 1 },
  { kind: 'weekday', re: new RegExp(`\\b(${FULL_DAY_NAMES_RE})\\b`, 'i'), group: 1 },
  { kind: 'sugar', re: /\bin\s+(?:a|an|\d+)\s+(?:day|days|week|weeks|month|months|year|years)\b/i },
  { kind: 'sugar', re: /\b\d+\s+(?:day|days|week|weeks|month|months|year|years)\s+from\s+now\b/i },
  { kind: 'sugar', re: /\b(?:end|start|beginning|middle)\s+of\s+(?:the\s+)?(?:week|month|year)\b/i },
  { kind: 'sugar', re: /\b(?:this|coming|upcoming)\s+(?:week|month|weekend)\b/i },
];

function dateInSentence(text, ref) {
  for (const { kind, re, group } of SENTENCE_DATE_PHRASES) {
    const m = text.match(re);
    if (!m) continue;
    const phrase = m[group || 0];
    let date = null;
    if (kind === 'weekday') date = getThisOrNextDayOfWeek(ref, dayIndexOf(phrase));
    else if (kind === 'nextWeekday') date = getNextDayOfWeek(ref, dayIndexOf(phrase), true);
    else date = Sugar.Date.create(phrase, { future: true });
    if (!Sugar.Date.isValid(date) || !isReasonablyDated(date)) continue;
    return {
      text: m[0].trim(),
      date: Sugar.Date.endOfDay(date),
      isRecurring: false,
      frequency: null,
      daysOfWeek: null,
      dayOfMonth: null,
      repeatOnMonth: null,
      repeatOnLastDayOfMonth: false
    };
  }
  return null;
}

const AMOUNT_WORDS = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
function amountOf(raw) {
  const s = raw.toLowerCase().replace(/\s+/g, ' ').trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  if (s.includes('couple')) return 2;
  if (s.includes('few')) return 3;
  return AMOUNT_WORDS[s];
}

// A due date is the deadline, so a part of the week resolves to the END of that part:
// early = Tuesday, mid = Wednesday, late / end of = Friday. Weeks start on Sunday, like "next Friday" above.
const WEEK_PART_DAY = { early: 2, mid: 3, late: 5, end: 5 };

function relativeDate(text, ref) {
  const done = (phrase, date) => (Sugar.Date.isValid(date) && isReasonablyDated(date))
    ? { text: phrase, date: Sugar.Date.endOfDay(date), isRecurring: false, frequency: null, daysOfWeek: null,
        dayOfMonth: null, repeatOnMonth: null, repeatOnLastDayOfMonth: false }
    : null;

  const part = text.match(WEEK_PART_RE);
  if (part) {
    const key = /end/i.test(part[1]) ? 'end' : part[1].toLowerCase();
    const target = new Date(ref.getTime());
    target.setDate(ref.getDate() - ref.getDay() + (part[2].toLowerCase() === 'next' ? 7 : 0) + WEEK_PART_DAY[key]);
    const passed = Sugar.Date.endOfDay(target) < ref;
    return done(part[0].trim(), passed ? new Date(ref.getTime()) : target);
  }

  const m = text.match(RELATIVE_AMOUNT_RE);
  if (m) {
    const n = amountOf(m[1] || m[3]);
    const unit = (m[2] || m[4]).toLowerCase();
    if (n == null) return null;
    const from = new Date(ref.getTime());
    const date = unit === 'day' ? Sugar.Date.addDays(from, n)
      : unit === 'week' ? Sugar.Date.addWeeks(from, n)
      : unit === 'month' ? Sugar.Date.addMonths(from, n)
      : Sugar.Date.addYears(from, n);
    return done(m[0].trim(), date);
  }
  return null;
}

// parse(text, refDate): with a refDate, Sugar's own "now" is pointed at it for this call, so a
// caller (or a test) gets the same answer on any day. The real clock is restored afterwards.
export function parse(originalText, refDate) {
  if (!(refDate instanceof Date)) return parseText(originalText, refDate);
  const realNow = Sugar.Date.getOption('newDateInternal');
  Sugar.Date.setOption('newDateInternal', () => new Date(refDate.getTime()));
  try {
    return parseText(originalText, refDate);
  } finally {
    Sugar.Date.setOption('newDateInternal', realNow);
  }
}

function parseText(originalText, refDate) {
  // Input safety: null/undefined/empty input returns null without parsing.
  if (!originalText || typeof originalText !== 'string' || !originalText.trim()) {
    return null;
  }

  // Use provided reference date or current date
  const ref = refDate || new Date();

  // Test our patterns on this text
  testPatterns(originalText);

  // Special case for "this task is due on X of every month"
  if (originalText.toLowerCase().includes("due on") &&
      (originalText.toLowerCase().includes("of every month") || originalText.toLowerCase().includes("every month"))) {
    console.log(`Special case detected: "this task is due on X of every month"`);
    // Extract the day number using a simple regex
    const dayMatch = originalText.match(/(?:due|is due)\s+on\s+(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?(?:every|each)\s+month/i);
    if (dayMatch && dayMatch[1]) {
      const dayNum = parseInt(dayMatch[1], 10);
      console.log(`Extracted day number: ${dayNum}`);

      // Create a special result for this case
      if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= 31) {
        // Calculate the next occurrence of this day of month
        const nextDate = getNextDayOfMonth(ref, dayNum);
        console.log(`Next occurrence of day ${dayNum} will be: ${nextDate}`);

        let result = {
          text: originalText,
          date: Sugar.Date.endOfDay(nextDate),
          isRecurring: true,
          frequency: 'Monthly',
          daysOfWeek: null,
          dayOfMonth: dayNum,
          repeatOnMonth: null,
          repeatOnLastDayOfMonth: false
        };
        console.log(`Created special monthly recurring result with dayOfMonth=${dayNum}`);
        return result; // Return early with our special result
      }
    }
  }

  let result = null; // Initialize result

  // --- 1. Check for Explicit Recurrence Patterns FIRST ---
  // Define a prioritized list of patterns to check (most specific to most general)
  const prioritizedPatterns = [
    // Check specific day of month patterns first
    'taskDueOnDayOfEveryMonth',
    'ordinalDayOfEveryMonth',
    'everyMonthOnDayOfMonth',
    'dayOfMonthOfEveryMonth',
    // Check specific yearly patterns
    'dayMonthEveryYear', // New pattern for "on X month every year"
    'everyYearOnDayMonth',
    // Then check specific day of week patterns
    'everyMonthOnDayOfWeek',
    'everyDayOfWeek',
    'everyWeekOnDay',
    'dayOfWeekOfEveryWeek',
    'dayOfWeekEveryWeek',
    // Then check last day of month
    'lastDayOfEveryMonth',
    // Then check other specific patterns
    'biWeekly',
    // Finally check the most general patterns
    'everyDay',
    'everyWeek',
    'everyMonth',
    'everyYear'
  ];
  
  // Check patterns in priority order
  let matchedPattern = null;
  let matchResult = null;
  
  for (const patternName of prioritizedPatterns) {
    const regex = RECURRENCE_PATTERNS[patternName];
    const match = originalText.match(regex);
    if (match) {
      console.log(`Recurrence MATCH: [${patternName}] in text: "${originalText}"`);
      console.log(`Match details:`, match);
      logMatch(patternName, originalText, match); // Use existing log helper
      
      matchedPattern = patternName;
      matchResult = match;
      break; // Stop at the first match in priority order
    }
  }
  
  if (matchedPattern && matchResult) {
    result = { // Initialize result for recurrence
      text: matchResult[0], // The matched recurrence phrase
      date: null, // Will be calculated below
      isRecurring: true,
      frequency: null,
      daysOfWeek: null,
      dayOfMonth: null,
      repeatOnMonth: null,
      repeatOnLastDayOfMonth: false
    };

    // --- Apply specific logic based on matchedPattern ---
    try { // Add try-catch around calculations
      switch (matchedPattern) {
        case 'everyDay': {
          result.frequency = 'Daily';
          result.date = Sugar.Date.endOfDay(ref);
          if (result.date <= ref) {
             result.date = Sugar.Date.endOfDay(Sugar.Date.addDays(ref, 1));
          }
          break;
        }
        case 'everyDayOfWeek':
        case 'dayOfWeekEveryWeek': {
           const dayNameW = (matchedPattern === 'everyDayOfWeek' ? matchResult[2] : matchResult[1]).toLowerCase();
           if (DAYS_OF_WEEK.hasOwnProperty(dayNameW)) {
             const targetDayW = DAYS_OF_WEEK[dayNameW];
             result.frequency = 'Weekly';
             result.daysOfWeek = [targetDayW];
             result.date = Sugar.Date.endOfDay(getThisOrNextDayOfWeek(ref, targetDayW));
           } else { throw new Error(`Invalid day name: ${dayNameW}`); }
           break;
        }
        case 'everyWeek': {
           result.frequency = 'Weekly';
           result.date = Sugar.Date.endOfDay(Sugar.Date.addWeeks(ref, 1));
           result.daysOfWeek = [result.date.getDay()];
           break;
        }
        case 'everyWeekOnDay': {
           // Handle both "every week on X" and "weekly on X" patterns
           let dayNameWON;
           if (matchResult[2]) {
             dayNameWON = matchResult[2].toLowerCase();
           } else if (matchResult[4]) {
             dayNameWON = matchResult[4].toLowerCase();
           } else {
             throw new Error('Could not extract day name from match');
           }
           
           if (DAYS_OF_WEEK.hasOwnProperty(dayNameWON)) {
             const targetDayWON = DAYS_OF_WEEK[dayNameWON];
             result.frequency = 'Weekly';
             result.daysOfWeek = [targetDayWON];
             result.date = Sugar.Date.endOfDay(getThisOrNextDayOfWeek(ref, targetDayWON));
           } else { throw new Error(`Invalid day name: ${dayNameWON}`); }
           break;
        }
        case 'biWeekly': {
           result.frequency = 'Bi-Weekly';
           result.date = Sugar.Date.endOfDay(Sugar.Date.addWeeks(ref, 2));
           result.daysOfWeek = [result.date.getDay()];
           break;
        }
        case 'ordinalDayOfEveryMonth':
        case 'everyMonthOnDayOfMonth':
        case 'taskDueOnDayOfEveryMonth': {
           // Handle different patterns for extracting the day of month
           let dayOfMonthODM;
           if (matchedPattern === 'ordinalDayOfEveryMonth' || matchedPattern === 'taskDueOnDayOfEveryMonth') {
               dayOfMonthODM = parseInt(matchResult[1], 10);
           } else { // everyMonthOnDayOfMonth
               dayOfMonthODM = parseInt(matchResult[2], 10);
           }
           console.log(`Monthly recurrence pattern matched: ${matchedPattern}, extracted day: ${dayOfMonthODM}, from match: ${JSON.stringify(matchResult)}`);
           if (!isNaN(dayOfMonthODM) && dayOfMonthODM >= 1 && dayOfMonthODM <= 31) {
             result.frequency = 'Monthly';
             result.dayOfMonth = dayOfMonthODM;
             console.log(`Setting result.dayOfMonth = ${dayOfMonthODM}`);
             result.date = Sugar.Date.endOfDay(getNextDayOfMonth(ref, dayOfMonthODM));
             console.log(`After setting result.date, dayOfMonth is still ${result.dayOfMonth}`);
           } else {
               // Get the appropriate match group based on the pattern
               const matchGroup = (matchedPattern === 'ordinalDayOfEveryMonth' || matchedPattern === 'taskDueOnDayOfEveryMonth') ? 1 : 2;
               throw new Error(`Invalid day of month: ${matchResult[matchGroup]}`);
           }
           break;
        }
        case 'lastDayOfEveryMonth': {
           result.frequency = 'Monthly';
           result.repeatOnLastDayOfMonth = true;
           
           // Calculate the last day of the current month
           const lastDay = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
           
           // If today is already the last day, move to next month
           if (ref.getDate() === lastDay.getDate()) {
             lastDay.setMonth(lastDay.getMonth() + 1);
             lastDay.setDate(0); // Set to last day of next month
           }
           
           result.date = Sugar.Date.endOfDay(lastDay);
           break;
        }
        case 'everyMonth': {
           result.frequency = 'Monthly';
           result.dayOfMonth = ref.getDate();
           result.date = Sugar.Date.endOfDay(Sugar.Date.addMonths(ref, 1));
           break;
        }
        case 'everyMonthOnDayOfWeek': {
           const ordinalStr = matchResult[2].toLowerCase();
           const dayNameMODW = matchResult[3].toLowerCase();
           
           if (DAYS_OF_WEEK.hasOwnProperty(dayNameMODW)) {
             const targetDayMODW = DAYS_OF_WEEK[dayNameMODW];
             let ordinalNum = 1; // Default to first
             
             // Convert ordinal word to number
             if (ordinalStr === 'first') ordinalNum = 1;
             else if (ordinalStr === 'second') ordinalNum = 2;
             else if (ordinalStr === 'third') ordinalNum = 3;
             else if (ordinalStr === 'fourth') ordinalNum = 4;
             else if (ordinalStr === 'last') ordinalNum = -1;
             
             result.frequency = 'Monthly';
             result.daysOfWeek = [targetDayMODW];
             result.dayOfMonth = ordinalNum; // Store the ordinal (1-4 or -1 for last)
             
             // Calculate the next occurrence
             const nextDate = getNextOrdinalDayOfWeekInMonth(ref, targetDayMODW, ordinalNum);
             result.date = Sugar.Date.endOfDay(nextDate);
           } else { throw new Error(`Invalid day name: ${dayNameMODW}`); }
           break;
        }
        case 'everyYear': {
           result.frequency = 'Yearly';
           result.dayOfMonth = ref.getDate();
           result.repeatOnMonth = ref.getMonth() + 1; // 1-12 for Jan-Dec
           result.date = Sugar.Date.endOfDay(Sugar.Date.addYears(ref, 1));
           break;
        }
        case 'everyYearOnDayMonth': {
           const dayNum = parseInt(matchResult[2], 10);
           const monthStr = matchResult[3].toLowerCase();
           
           if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= 31) {
             result.frequency = 'Yearly';
             result.dayOfMonth = dayNum;
             
             // Convert month string to month number (1-12)
             const monthMap = {jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, 
                              jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12};
             result.repeatOnMonth = monthMap[monthStr] || ref.getMonth() + 1;
             
             // Calculate the next occurrence
             const nextDate = getNextYearlyDate(ref, result.repeatOnMonth - 1, dayNum);
             result.date = Sugar.Date.endOfDay(nextDate);
           } else { throw new Error(`Invalid day of month: ${dayNum}`); }
           break;
        }
        case 'dayMonthEveryYear': {
           const dayNum = parseInt(matchResult[1], 10);
           const monthStr = matchResult[2].toLowerCase();
           
           if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= 31) {
             result.frequency = 'Yearly';
             result.dayOfMonth = dayNum;
             
             // Convert month string to month number (1-12)
             const monthMap = {jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, 
                              jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12};
             result.repeatOnMonth = monthMap[monthStr] || ref.getMonth() + 1;
             
             // Calculate the next occurrence
             const nextDate = getNextYearlyDate(ref, result.repeatOnMonth - 1, dayNum);
             result.date = Sugar.Date.endOfDay(nextDate);
           } else { throw new Error(`Invalid day of month: ${dayNum}`); }
           break;
        }
        default: {
           console.warn(`No specific calculation logic for pattern: ${matchedPattern}`);
           result = null; // Reset result if no specific logic
           break;
        }
      }
      
      if (result && result.date) {
          return result; // Return the structured recurrence result
      } else if (result === null) {
          // Calculation failed or pattern not handled, means we should stop and not fall back to Sugar
          return null;
      }
    } catch (error) {
      console.error(`Error calculating recurrence for pattern ${matchedPattern}:`, error);
      return null;
    }
  } // End recurrence loop

  // --- 1b. "3 days later", "early next week" — Sugar can't read these inside a sentence ---
  const relative = relativeDate(originalText, ref);
  if (relative) return relative;

  // --- 2. If NO recurrence returned/failed, try Sugar.js on FULL TEXT ---
  // Gate Sugar behind hasDateIntent(): without it, Sugar reads bare integers
  // as years and silently fills the due-date picker with garbage years.
  // Step 3 below still has its own SIMPLE_TIME_REGEX fallback; every phrase
  // SIMPLE_TIME_REGEX matches is also a hasDateIntent match, so gating here
  // does not block Step 3's fallback path.
  if (hasDateIntent(originalText)) {
    let parsedDateFull = Sugar.Date.create(originalText, { future: true });
    if (Sugar.Date.isValid(parsedDateFull) && isReasonablyDated(parsedDateFull)) {
      return {
        text: originalText,
        date: parsedDateFull,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null,
        repeatOnMonth: null,
        repeatOnLastDayOfMonth: false
      };
    }

    // Try without future: true on full text
    parsedDateFull = Sugar.Date.create(originalText);
    if (Sugar.Date.isValid(parsedDateFull) && isReasonablyDated(parsedDateFull)) {
      return {
        text: originalText,
        date: parsedDateFull,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null,
        repeatOnMonth: null,
        repeatOnLastDayOfMonth: false
      };
    }
  }

  // --- 3. If FULL TEXT failed, try ISOLATING a phrase ---
  
  // 3a. First try direct matching of common time expressions like "tomorrow"
  const directTimeMatch = originalText.match(SIMPLE_TIME_REGEX);
  if (directTimeMatch && directTimeMatch[1]) {
    let timePhrase = directTimeMatch[1].trim();
    console.log(`Direct time match found: "${timePhrase}"`);
    
    // Handle "tomorrow" and "(the) day after tomorrow" specifically
    const isDayAfterTomorrow = /^(?:the\s+)?day\s+after\s+tomorrow$/i.test(timePhrase);
    if (isDayAfterTomorrow || timePhrase.toLowerCase() === 'tomorrow') {
      const tomorrowDate = new Date(ref.getTime()); // Clone the reference date
      tomorrowDate.setDate(tomorrowDate.getDate() + (isDayAfterTomorrow ? 2 : 1));
      const parsedDate = Sugar.Date.endOfDay(tomorrowDate);
      console.log(`Parsed 'tomorrow' to: ${parsedDate}`);
      // Create a single date result
      return {
        text: timePhrase,
        date: parsedDate,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null,
        repeatOnMonth: null,
        repeatOnLastDayOfMonth: false
      };
    }
    
    // Handle "next [day]" expressions
    const nextDayMatch = timePhrase.match(/^next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/i);
    if (nextDayMatch) {
      const dayName = nextDayMatch[1].toLowerCase();
      const targetDay = DAYS_OF_WEEK[dayName];
      const nextDate = getNextDayOfWeek(ref, targetDay, true); // Force next week
      const parsedDate = Sugar.Date.endOfDay(nextDate);
      console.log(`Parsed '${timePhrase}' to: ${parsedDate}`);
      // Create a single date result
      return {
        text: timePhrase,
        date: parsedDate,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null,
        repeatOnMonth: null,
        repeatOnLastDayOfMonth: false
      };
    }
    
    // For other time expressions, try Sugar
    let parsedDate = Sugar.Date.create(timePhrase, { future: true });
    if (Sugar.Date.isValid(parsedDate)) {
      console.log(`Parsed '${timePhrase}' with Sugar to: ${parsedDate}`);
      // Create a single date result
      return {
        text: timePhrase,
        date: parsedDate,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null,
        repeatOnMonth: null,
        repeatOnLastDayOfMonth: false
      };
    }
  }

  // 3b. A date phrase inside a sentence: "Call Mrs. Smith by Friday", "call by Oct 12", "in 3 days".
  const inSentence = dateInSentence(originalText, ref);
  if (inSentence) return inSentence;

  // --- 4. If nothing worked ---
  return null; // Return null if no valid date or recurrence was found
}

// Export the parse function as the default export
export default {
  parse,
  hasDateIntent
};
